import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  MessageSquare, 
  FileText, 
  Info,
  Activity,
  Star,
  Users,
  BarChart3,
  CheckSquare,
  TrendingUp,
  StickyNote,
  MoreHorizontal,
  Send
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Lead } from '@/types/lead';
import NotesTabContent from './NotesTabContent';
import DocumentsTabContent from './DocumentsTabContent';
import OverviewTabContent from './OverviewTabContent';
import ActivityTabContent from './ActivityTabContent';
import ReviewsTabContent from './ReviewsTabContent';
import KeyContactsTabContent from './KeyContactsTabContent';
import ContactsTabContent from './ContactsTabContent';
import ScoreTabContent from './ScoreTabContent';
import TaskPanel from '@/components/tasks/panel/TaskPanel';
import OutreachTab from './tabs/OutreachTab';

interface TabContentSectionProps {
  lead: Lead;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onAddNote: (content: string, tags?: string[]) => Promise<void>;
  onUpdateNote: (noteId: string, updates: { content: string; tags: string[] }) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
  onAddDocument: (name: string, url: string, type: string, description?: string) => Promise<any>;
  onContactsUpdated: () => void;
  onDocumentDeleted: () => void;
  isAddDocumentOpen: boolean;
  onAddDocumentOpenChange: (open: boolean) => void;
  availableTags: string[];
  onLeadUpdated?: () => void;
}

const TabContentSection: React.FC<TabContentSectionProps> = ({
  lead,
  activeTab,
  setActiveTab,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onAddDocument,
  onContactsUpdated,
  onDocumentDeleted,
  isAddDocumentOpen,
  onAddDocumentOpenChange,
  availableTags,
  onLeadUpdated
}) => {
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="flex items-center gap-2 mb-2 px-4 md:px-6 border-b overflow-x-auto scrollbar-hide">
        <TabsList className="flex-1 justify-start bg-transparent h-auto p-0 border-0 min-w-max">
          {/* Primary Tabs - Always Visible */}
          <TabsTrigger 
            value="overview" 
            className="flex items-center gap-2 px-2 sm:px-3 py-2 whitespace-nowrap rounded-t-md border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground min-h-[40px]"
          >
            <Info className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">Overview</span>
          </TabsTrigger>
          <TabsTrigger 
            value="notes" 
            className="flex items-center gap-2 px-2 sm:px-3 py-2 whitespace-nowrap rounded-t-md border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground min-h-[40px]"
          >
            <StickyNote className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">Notes</span>
          </TabsTrigger>
          <TabsTrigger 
            value="tasks" 
            className="flex items-center gap-2 px-2 sm:px-3 py-2 whitespace-nowrap rounded-t-md border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground min-h-[40px]"
          >
            <CheckSquare className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">Tasks</span>
          </TabsTrigger>
          <TabsTrigger 
            value="contacts" 
            className="flex items-center gap-2 px-2 sm:px-3 py-2 whitespace-nowrap rounded-t-md border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground min-h-[40px]"
          >
            <Users className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">Contacts</span>
          </TabsTrigger>
        </TabsList>
        
        {/* More Menu - Secondary Tabs */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9 gap-2 mr-2 flex-shrink-0 min-w-[44px]">
              <MoreHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline text-sm">More</span>
            </Button>
          </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setActiveTab('documents')}>
                    <FileText className="h-4 w-4 mr-2" />
                    <span>Documents</span>
                  </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setActiveTab('reviews')}>
              <Star className="h-4 w-4 mr-2" />
              <span>Reviews</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setActiveTab('activity')}>
              <Activity className="h-4 w-4 mr-2" />
              <span>Activity</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setActiveTab('outreach')}>
              <Send className="h-4 w-4 mr-2" />
              <span>Outreach</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setActiveTab('score')}>
              <TrendingUp className="h-4 w-4 mr-2" />
              <span>Score</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <TabsContent value="overview" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <OverviewTabContent lead={lead} onLeadUpdated={onLeadUpdated} />
      </TabsContent>
      
      <TabsContent value="notes" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <NotesTabContent 
          notes={lead.notes}
          onAddNote={onAddNote} 
          onUpdateNote={onUpdateNote}
          onDeleteNote={onDeleteNote}
          availableTags={availableTags}
        />
      </TabsContent>
      
      <TabsContent value="documents" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <DocumentsTabContent 
          documents={lead.documents}
          leadId={lead.id}
          onAddDocument={onAddDocument} 
          onDocumentDeleted={onDocumentDeleted}
          isAddDocumentOpen={isAddDocumentOpen}
          onAddDocumentOpenChange={onAddDocumentOpenChange}
        />
      </TabsContent>
      
      <TabsContent value="contacts" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <ContactsTabContent 
          lead={lead}
          onContactsUpdated={onContactsUpdated} 
        />
      </TabsContent>
      
      <TabsContent value="key-contacts" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <KeyContactsTabContent 
          contacts={lead.keyContacts} 
          leadId={lead.id}
          onContactsUpdated={onContactsUpdated} 
        />
      </TabsContent>
      
      <TabsContent value="activity" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <ActivityTabContent lead={lead} />
      </TabsContent>
      
      <TabsContent value="reviews" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <ReviewsTabContent lead={lead} />
      </TabsContent>
      
      <TabsContent value="score" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <ScoreTabContent lead={lead} />
      </TabsContent>
      
      <TabsContent value="tasks" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <TaskPanel leadId={lead.id} />
      </TabsContent>

      <TabsContent value="outreach" className="mt-3 px-4 md:px-6 pb-4 md:pb-6">
        <OutreachTab lead={lead} />
      </TabsContent>
    </Tabs>
  );
};

export default TabContentSection;
